import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  Input,
  Table,
  Badge,
} from "reactstrap";
import ReactDatetime from "components/CustomDatePicker";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import classNames from "classnames";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2FormFieldsCss } from "utils/theme2MultiSelect";
import { useGetReportDetail, useCreateReport, useUpdateReport, useGenerateReport, useDeleteReport } from "api/reports/service";
import ReportDetail from "../components/Reports/ReportDetail";
import ReportColumns from "../components/Reports/ReportColumns";
import ReportFilters from "../components/Reports/ReportFilters";
import ReportPermissions from "../components/Reports/ReportPermissions";
import ReportSchedule from "../components/Reports/ReportSchedule";
import ReportViewer from "./ReportViewer";
import ReportParameters from "../components/Reports/ReportParameters";
import { isStandardScheduleReport } from "../components/Reports/standardScheduleReports";
import { FooterButton } from "components/Footer/components";
import { BUTTON_STYLES, DRAWER_STYLES } from "components/Footer/constants";
import SubHeader from "components/Navbars/SubHeader";
import { pushRecentReport } from "utils/recentReports";

const parseReportFilters = (filters) => {
  if (!filters) return [];
  try {
    const filtersArray = typeof filters === "string" ? JSON.parse(filters) : filters;
    return Array.isArray(filtersArray) ? filtersArray : [];
  } catch (error) {
    console.error("Error parsing filters data:", error);
    return [];
  }
};

const isAcctIdParameter = (param) => {
  const fieldName = String(param?.field_name || "").toLowerCase();
  const paramId = String(param?.id || "").toLowerCase();
  return (
    fieldName === "acct id" ||
    fieldName === "acct_id" ||
    paramId === "param_acct_id" ||
    paramId === "acct_id"
  );
};

const extractAcctIdFromParameters = (params) => {
  const acctParam = params?.find(isAcctIdParameter);
  const value = acctParam?.value;
  if (value === null || value === undefined || value === "") return null;
  return String(value).trim() || null;
};

const applyAcctIdToParameters = (params, acctId) => {
  if (!acctId || !Array.isArray(params)) return params;
  return params.map((p) => (isAcctIdParameter(p) ? { ...p, value: acctId } : p));
};

const ReportAdd = ({ activeColor = "blue", account, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Get active color style
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const isEditMode = !!id;
  const isViewMode = location.search.includes('view=true');
  const recordAcctId = searchParams.get('acct_id');

  const reportsListPath = useMemo(() => {
    const tab = location.state?.reportsTab;
    return tab ? `/reports?tab=${encodeURIComponent(tab)}` : "/reports";
  }, [location.state?.reportsTab]);

  const navigateBackToReports = useCallback(() => {
    navigate(reportsListPath);
  }, [navigate, reportsListPath]);

  // Tab state management
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'DETAIL');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const tabScrollRef = useRef(null);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);
  const isTheme2 = shellThemeVariant === "2";
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? `${getTheme2FormFieldsCss({
            scopeSelector: ".report-add-page.report-add-page--theme2",
            fieldsSelector: ".report-add-form-fields",
          })}
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .card {
            background-color: #27293d !important;
            border: 1px solid #3A4049 !important;
            box-shadow: none !important;
          }
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .card .card-body,
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .card label {
            color: rgba(255, 255, 255, 0.9) !important;
          }
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .filter-card,
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .selected-filters-container,
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .glass-card,
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .table-item,
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .field-item,
          body:not(.white-content) .report-add-page.report-add-page--theme2 .report-add-form-fields .enum-container {
            border-color: #3A4049 !important;
          }
          body.white-content .report-add-page.report-add-page--theme2 .report-add-form-fields .card {
            border: 1px solid #EBEAED !important;
            box-shadow: none !important;
          }
          @media (max-width: 991.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.report-add-page.report-add-page--theme2.theme2-admin-content-compact.theme2-reports-inset {
              padding-top: calc(74px + 45px) !important;
            }
          }
          @media (max-width: 575.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.report-add-page.report-add-page--theme2.theme2-admin-content-compact.theme2-reports-inset {
              padding-top: calc(64px + 45px) !important;
            }
          }`
        : "",
    [isTheme2]
  );
  const useHorizontalMobileTabs = isMobile;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Viewer state management
  const [showViewer, setShowViewer] = useState(false);
  const [viewerInfo, setViewerInfo] = useState({});

  // Modal states
  const [deleteModal, setDeleteModal] = useState(false);
  const [isGeneratingStep, setIsGeneratingStep] = useState(false);
  const [activeGenFilters, setActiveGenFilters] = useState([]);

  // Get report detail for edit mode
  const { data: reportData, isLoading: isLoadingReport } = useGetReportDetail(id);
  const scheduleStandardReport = isStandardScheduleReport(reportData);
  const isRecordSpecificReport = !!reportData?.spec_record_type;

  useEffect(() => {
    if (!reportData?.id) return;
    const userKey = account?.id || account?.user?.id || account?.email || "anon";
    pushRecentReport(userKey, {
      id: reportData.id,
      name: reportData.name,
      type: reportData.type,
    });
  }, [reportData?.id, reportData?.name, reportData?.type, account]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (!tabFromUrl || !isEditMode || !reportData) return;

    if (reportData.type === 'standard') {
      if (scheduleStandardReport) {
        setActiveTab('SCHEDULE');
      } else if (tabFromUrl === 'PARAMETERS') {
        setActiveTab('PARAMETERS');
      }
      return;
    }

    if (reportData.is_edit !== false) {
      const validTabs = ['DETAIL', 'COLUMNS', 'FILTERS', 'PERMISSIONS', 'SCHEDULE'];
      if (validTabs.includes(tabFromUrl)) {
        setActiveTab(tabFromUrl);
      }
    }
  }, [searchParams, isEditMode, reportData, scheduleStandardReport]);

  // Initialize viewer info from report data
  useEffect(() => {
    if (reportData && isViewMode) {
      setViewerInfo({
        id: id,
        name: reportData?.name || 'Generated Report',
        description: reportData?.description || 'Inventory Report',
        primary_data_source: reportData?.primary_data_source || 'Inventory',
        columns: reportData?.columns || [],
        value: reportData?.value || null,
        type: reportData?.type || null,
        owner: (reportData?.owner && typeof reportData.owner === 'object')
          ? (reportData.owner.username || reportData.owner.full_name || 'System')
          : (reportData?.owner || 'System')
      });
    }
  }, [reportData, id, isViewMode]);

  // Mutations
  const createReportMutation = useCreateReport();
  const updateReportMutation = useUpdateReport();
  const generateReportMutation = useGenerateReport();
  const deleteReportMutation = useDeleteReport();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    primary_data_source: "",
    owner: null,
    folder: "",
    report_folder: null,
    record_type: t('reports.defaultValues.none'),
    summary_report: false,
  });

  const [columnsData, setColumnsData] = useState([]);
  const [filtersData, setFiltersData] = useState([]);
  const [scheduleFiltersData, setScheduleFiltersData] = useState([]);
  const [parametersData, setParametersData] = useState([]);
  const [permissionsData, setPermissionsData] = useState([]);

  useEffect(() => {
    if (!isRecordSpecificReport || !recordAcctId || isViewMode || parametersData.length === 0) return;
    setParametersData((prev) => {
      const currentAcctId = extractAcctIdFromParameters(prev);
      if (currentAcctId === recordAcctId) return prev;
      return applyAcctIdToParameters(prev, recordAcctId);
    });
  }, [isRecordSpecificReport, recordAcctId, isViewMode, parametersData.length]);

  const [errors, setErrors] = useState({});
  const [showValidationError, setShowValidationError] = useState(false);

  // Refs for child components
  const reportColumnsRef = useRef();
  const reportDetailRef = useRef();
  const reportFiltersRef = useRef();
  const reportParametersRef = useRef();
  const reportScheduleRef = useRef();
  const generateParametersRef = useRef();
  const generateFiltersRef = useRef();

  // Local floating action menu state (ReportAdd specific)
  const [actionsClosed, setActionsClosed] = useState(false);
  const [hoverStates, setHoverStates] = useState({ button: null });

  // Update form data when report data is loaded
  useEffect(() => {
    if (reportData && isEditMode) {
      setFormData({
        name: reportData.name || "",
        description: reportData.description || "",
        primary_data_source: reportData.primary_data_source || "",
        owner:
          reportData.owner && typeof reportData.owner === "object"
            ? reportData.owner.id
            : reportData.owner || null,
        folder: reportData.report_folder?.name || reportData.folder || "",
        report_folder: reportData.report_folder
          ? {
              id: reportData.report_folder.documentId || reportData.report_folder.id,
              documentId: reportData.report_folder.documentId || reportData.report_folder.id,
              name: reportData.report_folder.name,
              owner_folder: reportData.report_folder.owner_folder || null,
            }
          : null,
        record_type: reportData.record_type || t('reports.defaultValues.none'),
        summary_report: reportData.summary_report || false,
      });

      // Initialize filters data
      const parsedFilters = parseReportFilters(reportData.filters);
      setFiltersData(parsedFilters);
      setScheduleFiltersData(parsedFilters.filter((filter) => filter.is_active !== false));

      // Initialize parameters data
      if (reportData.parameters) {
        try {
          const parametersArray = typeof reportData.parameters === 'string'
            ? JSON.parse(reportData.parameters)
            : reportData.parameters;
          const parsed = Array.isArray(parametersArray) ? parametersArray : [];
          setParametersData(
            reportData.spec_record_type && recordAcctId
              ? applyAcctIdToParameters(parsed, recordAcctId)
              : parsed
          );
        } catch (error) {
          console.error('Error parsing parameters data:', error);
          setParametersData([]);
        }
      } else {
        setParametersData([]);
      }

      // Initialize permissions data
      if (reportData.permissons) {
        setPermissionsData(Array.isArray(reportData.permissons) ? reportData.permissons : []);
      } else {
        setPermissionsData([]);
      }


      if (scheduleStandardReport) {
        setActiveTab('SCHEDULE');
      } else if (reportData.type === 'standard') {
        setActiveTab('PARAMETERS');
      }
    }
  }, [reportData, isEditMode, recordAcctId]);

  useEffect(() => {
    if (scheduleStandardReport) {
      setScheduleFiltersData(filtersData.filter((filter) => filter.is_active !== false));
    }
  }, [filtersData, scheduleStandardReport]);

  // SCHEDULE tab'a ilk geçişte rapor filtrelerini yükle
  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (activeTab === "SCHEDULE" && prevTabRef.current !== "SCHEDULE") {
      setScheduleFiltersData(filtersData.filter((filter) => filter.is_active !== false));
    }
    prevTabRef.current = activeTab;
  }, [activeTab, filtersData]);


  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('reports.nameRequired');
    }

    if (!formData.primary_data_source) {
      newErrors.primary_data_source = t('reports.primaryDataSourceRequired');
    }

    setErrors(newErrors);
    setShowValidationError(Object.keys(newErrors).length > 0);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, options = {}) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!validateForm()) {
      return false;
    }

    try {
      // If we're on COLUMNS tab, call the ReportColumns save method
      if (activeTab === 'COLUMNS' && reportColumnsRef.current && typeof reportColumnsRef.current.handleSave === 'function') {
        await reportColumnsRef.current.handleSave(options);
        return true;
      }

      // If we're on FILTERS tab, call the ReportFilters save method
      if (activeTab === 'FILTERS' && reportFiltersRef.current && typeof reportFiltersRef.current.handleSave === 'function') {
        await reportFiltersRef.current.handleSave(options);
        return true;
      }

      // If we're on DETAIL tab, call the ReportDetail save method
      if (activeTab === 'DETAIL' && reportDetailRef.current && typeof reportDetailRef.current.handleSave === 'function') {
        await reportDetailRef.current.handleSave(options);
        return true;
      }

      // If we're on PARAMETERS tab, call the ReportParameters save method
      if (activeTab === 'PARAMETERS' && reportParametersRef.current && typeof reportParametersRef.current.handleSave === 'function') {
        await reportParametersRef.current.handleSave(options);
        return true;
      }

      // If we're on SCHEDULE tab, call the ReportSchedule save method
      if (activeTab === 'SCHEDULE' && reportScheduleRef.current && typeof reportScheduleRef.current.handleSave === 'function') {
        const saved = await reportScheduleRef.current.handleSave(options);
        if (!saved) return false;
        return true;
      }

      // If we're on PERMISSIONS tab, use the regular form submission below
      // since PERMISSIONS doesn't have its own handleSave yet


      // For other tabs, use the regular form submission
      const payload = {
        ...formData,
        owner:
          formData.owner && typeof formData.owner === "object"
            ? formData.owner.id
            : formData.owner || null,
        report_folder:
          formData.report_folder && typeof formData.report_folder === "object"
            ? formData.report_folder.id || formData.report_folder.documentId
            : formData.report_folder || null,
        folder:
          formData.report_folder && typeof formData.report_folder === "object"
            ? formData.report_folder.name || ""
            : formData.folder || "",
        columns: columnsData,
        filters: JSON.stringify(filtersData),
        parameters: JSON.stringify(parametersData),
        permissons: permissionsData,
      };


      if (isEditMode) {
        await updateReportMutation.mutateAsync({
          id: id,
          data: payload,
          hideNotification: options.hideNotification
        });
      } else {
        await createReportMutation.mutateAsync(payload);
      }
      return true;
    } catch (error) {
      console.error('Error saving report:', error);
      return false;
    }
  };

  const handleCancel = () => {
    navigateBackToReports();
  };


  const proceedToRecordSpecificGenerate = (filtersToUse) => {
    const acctId = extractAcctIdFromParameters(filtersToUse) || recordAcctId;
    const newSearchParams = new URLSearchParams(location.search);
    if (acctId) {
      newSearchParams.set('acct_id', acctId);
    }
    newSearchParams.set('view', 'true');
    setSearchParams(newSearchParams);
  };

  const proceedToGenerate = async (filtersToSave, isStandard = false) => {
    if (isRecordSpecificReport) {
      proceedToRecordSpecificGenerate(filtersToSave);
      return;
    }

    const payload = {
      ...formData,
      owner:
        formData.owner && typeof formData.owner === "object"
          ? formData.owner.id
          : formData.owner || null,
      report_folder:
        formData.report_folder && typeof formData.report_folder === "object"
          ? formData.report_folder.id || formData.report_folder.documentId
          : formData.report_folder || null,
      folder:
        formData.report_folder && typeof formData.report_folder === "object"
          ? formData.report_folder.name || ""
          : formData.folder || "",
      columns: columnsData,
      filters: isStandard ? JSON.stringify(filtersData) : JSON.stringify(filtersToSave),
      parameters: isStandard ? JSON.stringify(filtersToSave) : JSON.stringify(parametersData),
      permissons: permissionsData,
    };

    try {
      if (isEditMode) {
        await updateReportMutation.mutateAsync({
          id: id,
          data: payload,
          hideNotification: true
        });
      } else {
        await createReportMutation.mutateAsync(payload);
      }
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('view', 'true');
      setSearchParams(newSearchParams);
      setShowViewer(true);
    } catch (error) {
      console.error('Error saving report before generating:', error);
    }
  };

  const handleGenerateReport = async () => {
    if (isGeneratingStep) {
      await handleGenerateSubmit();
      return;
    }

    const isStandard = reportData?.type === 'standard';
    const standardUsesParameters =
      isStandard &&
      !scheduleStandardReport &&
      Array.isArray(reportData?.parameters) &&
      reportData.parameters.length > 0;

    let currentFilters = isStandard
      ? (scheduleStandardReport ? filtersData : (standardUsesParameters ? parametersData : filtersData))
      : filtersData;

    if (!isStandard && reportFiltersRef.current && typeof reportFiltersRef.current.getFilters === 'function') {
      try {
        currentFilters = reportFiltersRef.current.getFilters();
      } catch (error) {
        console.error('Validation error in filters:', error);
        return;
      }
    } else if (
      isStandard &&
      standardUsesParameters &&
      reportParametersRef.current &&
      typeof reportParametersRef.current.getFilters === 'function'
    ) {
      try {
        currentFilters = reportParametersRef.current.getFilters();
      } catch (error) {
        console.error('Validation error in parameters:', error);
        return;
      }
    }

    const activeFilters = currentFilters.filter(filter => filter.is_active === true || filter.is_active === "true" || filter.is_active === 1);

    if (isStandard && scheduleStandardReport) {
      await proceedToGenerate(filtersData, false);
      return;
    }

    if (isRecordSpecificReport && recordAcctId) {
      const sourceFilters = activeFilters.length > 0 ? activeFilters : currentFilters;
      await proceedToGenerate(applyAcctIdToParameters(sourceFilters, recordAcctId), isStandard);
      return;
    }

    if (activeFilters.length > 0) {
      const filtersForGenerate = isRecordSpecificReport && recordAcctId
        ? applyAcctIdToParameters(activeFilters, recordAcctId)
        : activeFilters;
      setActiveGenFilters(JSON.parse(JSON.stringify(filtersForGenerate)));
      setIsGeneratingStep(true);
    } else {
      await proceedToGenerate(currentFilters, isStandard);
    }
  };

  const handleGenerateSubmit = async () => {
    const isStandard = reportData?.type === 'standard';
    let currentFilters = isStandard ? parametersData : filtersData;

    if (!isStandard && reportFiltersRef.current && typeof reportFiltersRef.current.getFilters === 'function') {
      try {
        currentFilters = reportFiltersRef.current.getFilters();
      } catch (error) { }
    } else if (isStandard && reportParametersRef.current && typeof reportParametersRef.current.getFilters === 'function') {
      try {
        currentFilters = reportParametersRef.current.getFilters();
      } catch (error) { }
    }

    let updatedActiveFilters = activeGenFilters;
    const generateStepRef =
      reportData?.type === 'standard' ? generateParametersRef : generateFiltersRef;
    if (generateStepRef.current && typeof generateStepRef.current.getFilters === 'function') {
      try {
        updatedActiveFilters = generateStepRef.current.getFilters();
      } catch (error) {
        console.error("Validation error in generate filters", error);
        return; // Validation failed
      }
    }

    const mergedFilters = currentFilters.map(f => {
      const updatedActive = updatedActiveFilters.find(af => af.id === f.id);
      if (updatedActive) {
        return { ...f, value: updatedActive.value, operator: updatedActive.operator };
      }
      return f;
    });

    if (isStandard) {
      if (!isRecordSpecificReport) {
        setParametersData(mergedFilters);
      }
    } else {
      setFiltersData(mergedFilters);
    }

    setIsGeneratingStep(false);
    await proceedToGenerate(mergedFilters, isStandard);
  };

  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);
  useEffect(() => {
    const isAutoGenerate = searchParams.get('generate') === 'true';
    if (!isAutoGenerate || !reportData || hasAutoGenerated) return;

    const isStandard = reportData.type === 'standard';
    const standardUsesParameters =
      !scheduleStandardReport &&
      Array.isArray(reportData.parameters) &&
      reportData.parameters.length > 0;

    // Verilerin state'e yüklenmesini bekle
    let needsToWait = false;
    if (isStandard && scheduleStandardReport) {
      const hasSourceFilters = reportData.filters && reportData.filters !== '[]' && reportData.filters.length > 0;
      if (hasSourceFilters && filtersData.length === 0) needsToWait = true;
    } else if (isStandard && standardUsesParameters) {
      if (parametersData.length === 0) needsToWait = true;
    } else if (isStandard) {
      const hasSourceFilters = reportData.filters && reportData.filters !== '[]' && reportData.filters.length > 0;
      if (hasSourceFilters && filtersData.length === 0) needsToWait = true;
    } else {
      const hasSourceFilters = reportData.filters && reportData.filters !== '[]' && reportData.filters.length > 0;
      if (hasSourceFilters && filtersData.length === 0) needsToWait = true;
    }

    if (!needsToWait) {
      setHasAutoGenerated(true);

      const newParams = new URLSearchParams(searchParams);
      newParams.delete('generate');
      setSearchParams(newParams);

      // Delay slightly to ensure component refs are ready
      setTimeout(() => {
        handleGenerateReport();
      }, 300);
    }
  }, [searchParams, reportData, filtersData, parametersData, hasAutoGenerated, scheduleStandardReport]);

  const confirmDelete = async () => {
    if (id) {
      try {
        await deleteReportMutation.mutateAsync(id);
        setDeleteModal(false);
        navigateBackToReports();
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const cancelDelete = () => {
    setDeleteModal(false);
  };

  // Floating buttons handlers
  const handleHoverChange = useCallback((type, value) => {
    setHoverStates((prev) => ({ ...prev, [type]: value }));
  }, []);

  const getIconColor = useCallback(() => {
    return "#fff";
  }, []);

  const getButtonStyle = useCallback(
    (buttonId) => {
      const isHovered = hoverStates.button === buttonId;

      const baseStyle = {
        ...BUTTON_STYLES.base,
        backgroundColor: "#181b23",
        borderColor: "#fff",
        borderWidth: "1px",
        borderStyle: "solid",
        marginLeft: "4px",
        transform: "scale(1) rotate(0deg)",
        boxShadow: "none",
        filter: "none",
        zIndex: "auto",
      };

      const hoverStyle = isHovered
        ? {
          ...BUTTON_STYLES.hovered,
          backgroundColor: activeColorStyle.background,
          borderColor: activeColorStyle.borderColor,
          borderWidth: "3px",
          transform: "scale(1.1) rotate(0deg)",
          boxShadow:
            "0 8px 25px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 255, 255, 0.3)",
          filter: "brightness(1.2) contrast(1.1)",
          zIndex: 1000,
        }
        : {};

      return {
        ...baseStyle,
        ...hoverStyle,
      };
    },
    [hoverStates.button, activeColorStyle]
  );


  // Tab definitions
  let allTabs = [
    {
      id: 'DETAIL',
      translationKey: 'reports.tabs.detail',
      fallbackLabel: 'Detail',
    },
    {
      id: 'COLUMNS',
      translationKey: 'reports.tabs.columns',
      fallbackLabel: 'Columns',
    },
    {
      id: 'FILTERS',
      translationKey: 'reports.tabs.filters',
      fallbackLabel: 'Filters',
    },
    {
      id: 'PERMISSIONS',
      translationKey: 'reports.tabs.permissions',
      fallbackLabel: 'Permissions',
    },
    {
      id: 'SCHEDULE',
      translationKey: 'reports.tabs.schedule',
      fallbackLabel: 'Schedule',
    },
  ];

  if (scheduleStandardReport) {
    allTabs = [
      {
        id: 'SCHEDULE',
        translationKey: 'reports.tabs.schedule',
        fallbackLabel: 'Schedule',
      },
    ];
  } else if (reportData?.type === 'standard') {
    allTabs = [
      {
        id: 'PARAMETERS',
        translationKey: 'reports.tabs.parameters',
        fallbackLabel: 'Parameters',
      },
    ];
  } else if (reportData && reportData.is_edit === false) {
    // If not standard and no edit permission, only show Detail tab
    allTabs = allTabs.filter(tab => tab.id === 'DETAIL');
  }

  // Filter tabs based on edit/add mode
  const tabs = isEditMode ? allTabs : allTabs.filter(tab => tab.id === 'DETAIL');

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const checkTabScroll = useCallback(() => {
    const el = tabScrollRef.current;
    if (!el) {
      setCanScrollTabsRight(false);
      return;
    }
    setCanScrollTabsRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    if (!useHorizontalMobileTabs) {
      setCanScrollTabsRight(false);
      return undefined;
    }
    const runCheck = () => {
      requestAnimationFrame(checkTabScroll);
    };
    runCheck();
    const scrollEl = tabScrollRef.current;
    scrollEl?.addEventListener("scroll", checkTabScroll);
    window.addEventListener("resize", runCheck);
    return () => {
      scrollEl?.removeEventListener("scroll", checkTabScroll);
      window.removeEventListener("resize", runCheck);
    };
  }, [checkTabScroll, tabs.length, id, useHorizontalMobileTabs, isGeneratingStep]);

  const handleTabScrollRight = useCallback(() => {
    if (tabScrollRef.current && canScrollTabsRight) {
      tabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  }, [canScrollTabsRight]);

  if (isLoadingReport && isEditMode) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">{t('common.loading')}</span>
        </div>
        <span className="ml-2">{t('reports.loadingReportData')}</span>
      </div>
    );
  }

  // If viewer is shown, render ReportViewer component
  if (showViewer) {
    return (
      <ReportViewer
        activeColor={activeColor}
        reportInfo={viewerInfo}
        setShowViewer={setShowViewer}
      />
    );
  }

  // If in view mode, show ReportViewer
  if (isViewMode && isEditMode) {
    return (
      <ReportViewer
        reportInfo={viewerInfo}
        activeColor={activeColor}
        setShowViewer={setShowViewer}
      />
    );
  }

  return (
    <div
      className={classNames("content", "report-add-page", {
        "report-add-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-reports-inset": isTheme2,
      })}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      {/* Header Card */}

      <SubHeader
        activeColor={activeColor}
        onBack={navigateBackToReports}
        title={(reportData?.name || id)}
        status={reportData?.status}
      />
      {/* Tab Navigation */}
      <div
        className={
          useHorizontalMobileTabs
            ? "d-flex align-items-center justify-content-between mb-0 flex-wrap report-detail-tabs detail-mobile-tabs"
            : "d-flex align-items-center justify-content-between mb-0 flex-wrap report-detail-tabs"
        }
      >
        {useHorizontalMobileTabs ? (
          <div className="d-flex align-items-center flex-grow-1 min-width-0" style={{ minWidth: 0 }}>
            <div
              ref={tabScrollRef}
              className="flex-grow-1 channel-tabs-scroll"
              style={{ minWidth: 0 }}
            >
              <Nav
                tabs
                className="border-0"
                style={{
                  borderBottom: "none",
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  minWidth: "max-content",
                  width: "auto",
                  paddingLeft: 0,
                  paddingRight: 0,
                }}
              >
                {!isGeneratingStep
                  ? tabs.map((tab) => (
                  <NavItem key={tab.id} style={{ flex: "0 0 auto" }}>
                    <NavLink
                      href="#"
                      className={activeTab === tab.id ? "active font-weight-bold" : "text-muted"}
                      style={{
                        background: "none",
                        border: "none",
                        position: "relative",
                        cursor: tab.disabled ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                        color: tab.disabled ? "#6c757d" : (activeTab === tab.id ? activeColorStyle.borderColor : undefined),
                        fontWeight: activeTab === tab.id ? 700 : 500,
                        opacity: tab.disabled ? 0.5 : 1,
                        pointerEvents: tab.disabled ? "none" : "auto",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!tab.disabled) {
                          handleTabChange(tab.id);
                        }
                      }}
                    >
                      {t(tab.translationKey) || tab.fallbackLabel}
                      {activeTab === tab.id && (
                        <div
                          style={{
                            height: 3,
                            background: activeColorStyle.borderColor,
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: -8,
                          }}
                        />
                      )}
                    </NavLink>
                  </NavItem>
                ))
                  : null}
                {isGeneratingStep && (
                  <NavItem style={{ flex: "0 0 auto" }}>
                    <NavLink
                      href="#"
                      className="active font-weight-bold"
                      style={{
                        background: "none",
                        border: "none",
                        position: "relative",
                        whiteSpace: "nowrap",
                        color: activeColorStyle.borderColor,
                        fontWeight: 700,
                      }}
                      onClick={(e) => e.preventDefault()}
                    >
                      {t("reports.tabs.parameters") || "Parameters"}
                      <div
                        style={{
                          height: 3,
                          background: activeColorStyle.borderColor,
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: -8,
                        }}
                      />
                    </NavLink>
                  </NavItem>
                )}
              </Nav>
            </div>
            {canScrollTabsRight && (
              <button
                type="button"
                className="btn btn-link p-0 flex-shrink-0"
                onClick={handleTabScrollRight}
                style={{
                  color: activeColorStyle.borderColor,
                  lineHeight: 1,
                }}
                aria-label={t("common.scrollTabs")}
              >
                <i className="tim-icons icon-minimal-right" style={{ fontSize: 20 }} />
              </button>
            )}
          </div>
        ) : (
        <Nav tabs className="border-0" style={{ borderBottom: "none" }}>
          {!isGeneratingStep
            ? tabs.map(tab => (
            <NavItem key={tab.id}>
              <NavLink
                href="#"
                className={activeTab === tab.id ? "active font-weight-bold" : "text-muted"}
                style={{
                  background: "none",
                  border: "none",
                  position: "relative",
                  cursor: tab.disabled ? 'not-allowed' : 'pointer',
                  color: tab.disabled ? '#6c757d' : (activeTab === tab.id ? activeColorStyle.borderColor : undefined),
                  fontWeight: activeTab === tab.id ? 700 : 500,
                  opacity: tab.disabled ? 0.5 : 1,
                  pointerEvents: tab.disabled ? 'none' : 'auto'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  if (!tab.disabled) {
                    handleTabChange(tab.id);
                  }
                }}
              >
                {t(tab.translationKey) || tab.fallbackLabel}
                {activeTab === tab.id && (
                  <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                )}
              </NavLink>
            </NavItem>
          ))
            : null}
          {isGeneratingStep && (
            <NavItem>
              <NavLink
                href="#"
                className="active font-weight-bold"
                style={{
                  background: "none",
                  border: "none",
                  position: "relative",
                  color: activeColorStyle.borderColor,
                  fontWeight: 700
                }}
                onClick={(e) => e.preventDefault()}
              >
                {(scheduleStandardReport
                  ? t('reports.tabs.schedule')
                  : t('reports.tabs.parameters')) || (scheduleStandardReport ? 'Schedule' : 'Parameters')}
                <div style={{ height: 3, background: activeColorStyle.borderColor, position: "absolute", left: 0, right: 0, bottom: -8 }} />
              </NavLink>
            </NavItem>
          )}
        </Nav>
        )}

        <div className="d-flex align-items-center ml-auto">
          <Button
            type="button"
            color="secondary"
            onClick={() => isGeneratingStep ? setIsGeneratingStep(false) : handleCancel()}
            className="mr-2 btn-round"
            size="sm"
          >
            {t('common.cancel')}
          </Button>
          {!isGeneratingStep && (reportData?.is_edit || !isEditMode || scheduleStandardReport) && !isRecordSpecificReport && (
            <Button
              type="button"
              className="btn-round"
              style={activeColorStyle}
              onClick={handleSubmit}
              size="sm"
              disabled={createReportMutation.isPending || updateReportMutation.isPending}
            >
              {createReportMutation.isPending || updateReportMutation.isPending ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" />
                  {t('common.saving')}
                </>
              ) : (
                isEditMode ? t('common.update') : t('common.save')
              )}
            </Button>
          )}
        </div>
      </div>

      {reportData && (reportData.is_edit === false || !isEditMode) && (
        <Alert
          className="mt-3 mb-0"
          style={{
            backgroundColor: '#344675',
            border: 'none',
            color: '#ffffff',
            fontWeight: '500',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            padding: '15px 20px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}
        >
          <i className="fas fa-info-circle mr-3" style={{ fontSize: '18px', color: '#ffffff' }} />
          {t('reports.cannotEditMessage')}
        </Alert>
      )}

      {/* Tab Content */}
      {!isGeneratingStep ? (
        <TabContent
          activeTab={activeTab}
          className={classNames({ "report-add-form-fields": isTheme2 })}
        >
          {reportData?.type !== 'standard' && (
            <TabPane tabId="DETAIL">
              <ReportDetail
                ref={reportDetailRef}
                activeColor={activeColor}
                isTheme2={isTheme2}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                showValidationError={showValidationError}
                id={id}
                isEditMode={isEditMode}
                isEdit={reportData?.is_edit}
                permissions={permissionsData}
                reportType={reportData?.type}
                selectedTenantId={account?.selected_tenant || null}
              />
            </TabPane>

          )}

          {isEditMode && reportData?.type !== 'standard' && (
            <>
              <TabPane tabId="COLUMNS">
                <ReportColumns
                  ref={reportColumnsRef}
                  activeColor={activeColor}
                  isTheme2={isTheme2}
                  data={reportData?.columns}
                  id={id}
                  onColumnsChange={setColumnsData}
                  permissions={permissionsData}
                />
              </TabPane>

              <TabPane tabId="FILTERS">
                <ReportFilters
                  ref={reportFiltersRef}
                  activeColor={activeColor}
                  isTheme2={isTheme2}
                  id={id}
                  onFiltersChange={setFiltersData}
                  data={reportData?.filters}
                  permissions={permissionsData}
                />
              </TabPane>

              <TabPane tabId="PERMISSIONS">
                <ReportPermissions
                  activeColor={activeColor}
                  isTheme2={isTheme2}
                  permissions={permissionsData}
                  setPermissionsData={setPermissionsData}
                />
              </TabPane>


              <TabPane tabId="SCHEDULE">
                <ReportSchedule
                  ref={reportScheduleRef}
                  activeColor={activeColor}
                  isTheme2={isTheme2}
                  id={id}
                  permissions={permissionsData}
                  columns={columnsData?.length ? columnsData : (reportData?.columns || [])}
                  filters={scheduleFiltersData}
                  onFiltersChange={setScheduleFiltersData}
                  initialScheduleData={reportData?.report_schedule}
                />
              </TabPane>
            </>
          )}

          {isEditMode && scheduleStandardReport && (
            <TabPane tabId="SCHEDULE">
              <ReportSchedule
                ref={reportScheduleRef}
                activeColor={activeColor}
                isTheme2={isTheme2}
                id={id}
                permissions={permissionsData}
                columns={reportData?.columns || []}
                filters={scheduleFiltersData}
                onFiltersChange={setScheduleFiltersData}
                initialScheduleData={reportData?.report_schedule}
                lockFilters
              />
            </TabPane>
          )}

          {isEditMode && reportData?.type === 'standard' && !scheduleStandardReport && (
            <TabPane tabId="PARAMETERS">
              <ReportParameters
                ref={reportParametersRef}
                activeColor={activeColor}
                isTheme2={isTheme2}
                id={id}
                onParametersChange={setParametersData}
                data={reportData?.parameters}
                permissions={permissionsData}
              />
            </TabPane>
          )}
        </TabContent>
      ) : (
        <div className={classNames("mt-0", { "report-add-form-fields": isTheme2 })}>
          {reportData?.type === 'standard' ? (
            <ReportParameters
              ref={generateParametersRef}
              activeColor={activeColor}
              isTheme2={isTheme2}
              id={id}
              data={activeGenFilters}
              permissions={permissionsData}
            />
          ) : (
            <ReportFilters
              ref={generateFiltersRef}
              activeColor={activeColor}
              isTheme2={isTheme2}
              id={id}
              data={activeGenFilters}
              permissions={permissionsData}
            />
          )}
        </div>
      )}

      {/* Floating Action Buttons - ReportAdd */}
      <div style={DRAWER_STYLES.floatingContainer}>
        {isEditMode && (
          <>
            <FooterButton
              id="generate-report"
              icon={
                generateReportMutation.isPending || updateReportMutation.isPending ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  <i className="fas fa-play" />
                )
              }
              onClick={async (e) => {
                handleGenerateReport();
              }}
              onHoverChange={(isHovered) =>
                handleHoverChange("button", isHovered ? "generate-report" : null)
              }
              style={{
                ...getButtonStyle("generate-report"),
                backgroundColor: generateReportMutation.isPending || updateReportMutation.isPending ? "#6c757d" : "green",
                backgroundImage: "none",
                borderColor: generateReportMutation.isPending || updateReportMutation.isPending ? "#6c757d" : "green",
              }}
              iconColor={getIconColor()}
            />

            {reportData?.is_edit && (
              <FooterButton
                id="delete-report"
                className="customer-remove-btn"
                icon={<i className="fas fa-trash" />}
                onClick={() => {
                  setDeleteModal(true);
                }}
                onHoverChange={(isHovered) =>
                  handleHoverChange("button", isHovered ? "delete-report" : null)
                }
                style={{
                  backgroundColor: activeColorStyle.borderColor,
                  borderColor: activeColorStyle.borderColor,
                }}
                iconColor={getIconColor()}
              />
            )}
          </>
        )}

        <FooterButton
          id="actions-toggle"
          icon={
            <i
              className={`tim-icons ${actionsClosed ? "icon-minimal-up" : "icon-minimal-down"
                }`}
            />
          }
          onHoverChange={(isHovered) =>
            handleHoverChange("button", isHovered ? "actions-toggle" : null)
          }
          onClick={() => setActionsClosed(!actionsClosed)}
          style={getButtonStyle("actions-toggle")}
          iconColor={getIconColor()}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={cancelDelete} centered>
        <ModalHeader toggle={cancelDelete}>
          {t('reports.deleteReport')}
        </ModalHeader>
        <ModalBody>
          {t('reports.deleteConfirmMessage')}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={cancelDelete}
            disabled={deleteReportMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color="danger"
            onClick={confirmDelete}
            disabled={deleteReportMutation.isPending}
            style={activeColorStyle}
          >
            {deleteReportMutation.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ReportAdd;
